import {
  PRINT_LOGO_BYTES_PER_ROW,
  PRINT_LOGO_WIDTH_DOTS,
  PRINT_RECEIPT_MAX_HEIGHT_DOTS,
  type PrintReceiptRaster,
} from '@gc/shared';

import { packToPrinterBits, toBase64 } from '@/features/settings/printLogo';

/**
 * A tall receipt painted in SEGMENTS, packed into ONE raster (2026-09-28).
 *
 * WHY. Receipts taller than 4,096 dots (~51 cm) used to be refused here and
 * printed typed — and the owner saw it: "on long receipts the lines between
 * the orders become dashed, but on shorter ones it's perfect". The ceiling is
 * 16,384 dots now (~2 m, `PRINT_RECEIPT_MAX_HEIGHT_DOTS`), and a canvas that
 * tall is exactly what an iPad will not give us: Safari caps a canvas's area
 * (~16.7 M px) and, worse, the TOTAL memory of live canvases, and a refused
 * canvas does not throw — it paints nothing. So the receipt is painted at most
 * 4,096 rows at a time, each segment packed to printer bits on its own, and
 * the packed rows are joined. The server receives the same single raster it
 * always did (`widthDots`, `heightDots`, `data`) and cannot tell.
 *
 * Everything here is pure — the canvas work is injected as `paintSegment` —
 * because this workspace's tests run in plain node with no DOM. The painter in
 * `receiptCanvas.ts` is the only part that touches a canvas.
 */

/** Rows per painted segment: 576 × 4,096 is 2.4 M px, 9.4 MB of RGBA. */
export const RASTER_SEGMENT_ROWS = 4_096;

/**
 * How far past its own box a block may be drawn and still be painted.
 *
 * A glyph can overhang its line box — an Arabic mark above, a descender below —
 * and a block straddling a segment edge must be painted into BOTH segments so
 * each gets its half. 64 dots is well past the tallest font on the receipt
 * (34 px); drawing a block into a segment it does not touch costs nothing but
 * a clipped call.
 */
export const SEGMENT_BLEED_DOTS = 64;

export interface RasterSegment {
  /** First row of the receipt this segment paints. */
  top: number;
  /** Rows in this segment; the last one is usually shorter. */
  height: number;
}

/** Can a layout this tall be drawn at all? Taller prints typed instead. */
export function isDrawableHeight(heightDots: number): boolean {
  return (
    Number.isInteger(heightDots) && heightDots >= 1 && heightDots <= PRINT_RECEIPT_MAX_HEIGHT_DOTS
  );
}

/** `heightDots` rows cut into consecutive segments of at most `maxRows`. */
export function rasterSegments(
  heightDots: number,
  maxRows: number = RASTER_SEGMENT_ROWS,
): RasterSegment[] {
  const segments: RasterSegment[] = [];
  if (!(heightDots >= 1) || !(maxRows >= 1)) return segments;
  for (let top = 0; top < heightDots; top += maxRows) {
    segments.push({ top, height: Math.min(maxRows, heightDots - top) });
  }
  return segments;
}

/** The blocks that can put ink into `segment` — anything within the bleed of it. */
export function blocksForSegment<T extends { y: number; height: number }>(
  blocks: readonly T[],
  segment: RasterSegment,
  bleed: number = SEGMENT_BLEED_DOTS,
): T[] {
  const top = segment.top - bleed;
  const bottom = segment.top + segment.height + bleed;
  return blocks.filter((block) => block.y < bottom && block.y + block.height > top);
}

/**
 * Packed segments -> the whole receipt's packed rows, in order.
 *
 * Throws if the rows do not add up: a picture short by a row prints as a
 * receipt that stops early, and one long by a row shifts everything after it.
 * The server refuses both; better never to send one.
 */
export function joinPackedSegments(
  parts: readonly Uint8Array[],
  heightDots: number,
  bytesPerRow: number = PRINT_LOGO_BYTES_PER_ROW,
): Uint8Array {
  const expected = heightDots * bytesPerRow;
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  if (total !== expected) {
    throw new Error(`packed segments are ${total} bytes, expected ${expected}`);
  }

  const joined = new Uint8Array(expected);
  let at = 0;
  for (const part of parts) {
    joined.set(part, at);
    at += part.length;
  }
  return joined;
}

/**
 * Paints `segment` and answers its RGBA pixels, `PRINT_LOGO_WIDTH_DOTS` wide
 * and `segment.height` tall — or null when the browser will not paint it.
 */
export type PaintSegment = (segment: RasterSegment) => Uint8ClampedArray | null;

/**
 * A receipt `heightDots` tall as ONE raster, painted a segment at a time.
 *
 * Null — never a throw — when it cannot be drawn: too tall, a canvas refused,
 * or pixels of the wrong size back from one. The typed receipt is a perfectly
 * good fallback and is already queued; a failed drawing must cost nothing.
 */
export function rasterFromSegments(
  heightDots: number,
  paintSegment: PaintSegment,
): PrintReceiptRaster | null {
  if (!isDrawableHeight(heightDots)) return null;

  const packed: Uint8Array[] = [];
  for (const segment of rasterSegments(heightDots)) {
    const rgba = paintSegment(segment);
    // A canvas the browser would not size comes back small or empty rather
    // than failing; packing it would print blank paper where the rows were.
    if (!rgba || rgba.length !== PRINT_LOGO_WIDTH_DOTS * segment.height * 4) return null;
    packed.push(packToPrinterBits(rgba, PRINT_LOGO_WIDTH_DOTS, segment.height));
  }

  return {
    widthDots: PRINT_LOGO_WIDTH_DOTS,
    heightDots,
    data: toBase64(joinPackedSegments(packed, heightDots)),
  };
}
